/**
 * Represents a normalized ISO-8601 UTC timestamp string, e.g. YYYY-MM-DDTHH:mm:ss.sssZ
 */
export type UTCTimestamp = string & { readonly __brand: unique symbol };

/**
 * Normalizes any date-time string to ISO-8601 UTC format.
 * If no timezone offset or Z suffix is present, it is appended to assume UTC.
 */
export const toUTCTimestamp = (dateTimeStr: string): UTCTimestamp => {
  if (!dateTimeStr) return "" as UTCTimestamp;
  let normalized = dateTimeStr.trim();
  
  if (!normalized.includes("Z") && !normalized.includes("+") && !normalized.includes("GMT")) {
    normalized = normalized.replace(" ", "T");
    if (normalized.includes("T")) {
      if (!normalized.endsWith("Z")) {
        normalized += "Z";
      }
    } else {
      normalized += "T00:00:00.000Z";
    }
  }
  return normalized as UTCTimestamp;
};

/**
 * Formats any timestamp string into the user's local timezone.
 * Auto-detects runtime timezone and supports DST automatically.
 * Returns formatted string as 'YYYY-MM-DD HH:mm:ss'
 */
export const formatTimestamp = (dateTimeStr: string | null | undefined): string => {
  if (!dateTimeStr) return "N/A";

  try {
    const utcTimestamp = toUTCTimestamp(dateTimeStr);
    const date = new Date(utcTimestamp);

    if (isNaN(date.getTime())) {
      return dateTimeStr;
    }

    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: localTimeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    });

    const parts = formatter.formatToParts(date);
    const partMap = {} as Record<string, string>;
    for (const part of parts) {
      partMap[part.type] = part.value;
    }

    return `${partMap.year}-${partMap.month}-${partMap.day} ${partMap.hour}:${partMap.minute}:${partMap.second}`;
  } catch (e) {
    return dateTimeStr;
  }
};
